import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from '../../../util/reactIntl';
import { uploadRoomMockup } from '../../../util/api';
// Mock API imports for fast testing (toggle MOCK_MODE in mockup-api-mock.js)
import {
  MOCK_MODE,
  mockUploadRoomMockup,
} from '../../../util/mockup-api-mock';
import {
  startMockupUpload,
  triggerMockupPolling,
  setUploadedImage,
  selectMockupUpload,
  selectPreviewImage,
} from '../../../ducks/mockupUpload.duck';
import { IconCamera } from '../../../components';
import {
  getArtworkShadow,
  getArtworkBorder,
} from '../../../util/artworkDimensions';
import {
  saveRequest,
  saveListingContext,
} from '../../../util/mockup-manager';
import { createSlug } from '../../../util/urlHelpers';
import { useTrackEvent } from '../../../context/AnalyticsContext';

// Cycling processing messages
const PROCESSING_MESSAGES = [
  'Processing your image',
  'This might take a minute',
  'Creating your preview',
  'Finding the perfect spot',
  'Teaching walls to smile',
  'Adjusting for lighting and layout',
  'Matching scale and lighting',
  'Just a moment',
  'Adding the finishing touches',
];

const RoomMockupUpload = ({ currentUser, onUploadSuccess, artworkImage, artworkVariants, listing }) => {
  const dispatch = useDispatch();
  const { isUploading, error, progressStatus } = useSelector(selectMockupUpload);
  const previewImage = useSelector(selectPreviewImage);
  const [localError, setLocalError] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const { trackEvent } = useTrackEvent();

  // Cycle through messages every 5 seconds while uploading
  React.useEffect(() => {
    if (isUploading) {
      const intervalId = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % PROCESSING_MESSAGES.length);
      }, 5000);

      return () => clearInterval(intervalId);
    } else {
      setMessageIndex(0);
    }
  }, [isUploading]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setLocalError('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
      return;
    }

    setLocalError(null);

    // Create data URL for immediate preview (show blurred preview instantly)
    const reader = new FileReader();
    reader.onload = () => {
      dispatch(setUploadedImage(reader.result));
    };
    reader.readAsDataURL(file);

    try {
      // Generate unique job ID using native crypto API
      const jobId = crypto.randomUUID();

      // Check if this is a variant listing and get parent listing ID if so
      const isVariant = listing?.attributes?.publicData?.isVariant === true;
      const parentId = listing?.attributes?.publicData?.parentId;
      // Use parent listing ID if this is a variant, otherwise use the listing's own ID
      const listingId = (isVariant && parentId) ? parentId : listing.id.uuid;

      // Create listing context
      // Note: Variants typically use the parent's title (as seen in ListingPageCarousel),
      // so we use the current listing's title which should already be the parent's title
      const listingContext = {
        listingId: listingId,
        listingSlug: createSlug(listing.attributes.title),
        listingTitle: listing.attributes.title,
      };

      // Save request to localStorage
      saveRequest(jobId);
      saveListingContext(jobId, listingContext);

      // Dispatch global upload start action (sets isUploading to true)
      dispatch(startMockupUpload(jobId, listingContext));

      // Upload to backend API with jobId
      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobId', jobId);

      // Use mock API if MOCK_MODE is enabled, otherwise use real API
      const uploadFunction = MOCK_MODE ? mockUploadRoomMockup : uploadRoomMockup;

      // Upload returns immediately with 202 Accepted
      await uploadFunction(formData);

      // Track mockup upload started event
      trackEvent('mockup_upload_started', {
        listing_id: listingId,
        listing_title: listing.attributes.title,
        is_variant: isVariant,
        is_authenticated: !!currentUser?.id,
      });

      // Trigger polling in MockupPollManager
      dispatch(triggerMockupPolling(jobId));
    } catch (err) {
      console.error('[RoomMockupUpload] Upload error:', err);
      setLocalError('Failed to upload image. Please try again.');
    }
  };

  // Get the artwork image URL from variants
  const variants = artworkImage?.attributes?.variants;
  const preferredVariants = ['scaled-xlarge', 'scaled-large', 'scaled-medium', 'scaled-small'];

  let artworkUrl = null;
  for (const variantName of preferredVariants) {
    if (variants?.[variantName]?.url) {
      artworkUrl = variants[variantName].url;
      break;
    }
  }

  // Calculate shadow and border styles
  // For grid mockup, use a fixed scale factor since we don't have wall segmentation data
  const scaleFactor = 1;
  const borderStyle = getArtworkBorder(scaleFactor);

  // When uploading and we have a preview image, show the full-screen blurred preview
  if (isUploading && previewImage) {
    return (
      <div className="w-full h-full relative overflow-hidden">
        {/* Blurred preview image */}
        <img
          src={previewImage.url}
          alt="Processing preview"
          className="w-full h-full object-cover"
          style={{
            filter: `blur(${previewImage.blur}px)`,
            transition: 'filter 0.5s ease-out',
            transform: 'scale(1.1)', // Slightly scale up to avoid blur edge artifacts
          }}
        />

        {/* Centered loader overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* Processing message */}
          <div className="bg-black/60 rounded-full px-6 py-2">
            <span
              key={messageIndex}
              className="text-white text-sm md:text-base font-medium"
            >
              {PROCESSING_MESSAGES[messageIndex]}...
            </span>
          </div>

          {/* Progress indicator */}
          {previewImage.blur < 20 && (
            <div className="mt-4 flex items-center gap-2">
              {[0, 1, 2].map((idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    previewImage.blur <= [16, 8, 4][idx]
                      ? 'bg-white'
                      : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {(localError || error) && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-red-500 text-white rounded-lg text-center text-sm">
            {localError || error}
          </div>
        )}
      </div>
    );
  }

  // Normal upload UI (not uploading)
  return (
    <div
      className="w-full h-full flex flex-col relative"
    >
      {/* Subtle purple gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 60% at center, rgba(124, 51, 234, 0.12) 0%, transparent 100%)',
        }}
      />

      {/* Visualization Area - 60% of height with grid background */}
      <div
        className="relative w-full md:h-[40%] h-[30%] overflow-hidden"
      >
        {/* Grid background with 20% opacity */}
        <div
          className="absolute inset-0 mx-8"
          style={{
            backgroundImage: "url('/static/images/gridBG.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.8,
          }}
        />

        {/* Artwork centered in visualization area */}
        {artworkImage && artworkUrl && (
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={artworkUrl}
              alt="Artwork"
              className="object-contain border-b-1 border-b-gray-300 h-16 md:h-28 w-auto shadow-sm md:shadow-md brightness-110"
            />
          </div>
        )}
      </div>

      {/* Smart Preview Pill - Positioned halfway between top and bottom sections */}
      {/* Mobile: at 30% boundary */}
      <div
        className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center w-max px-2 rounded-full z-10 md:hidden"
        style={{
          backgroundColor: 'rgba(124, 51, 234, 0.2)',
          top: '30%',
        }}
      >
        <img
          src="/static/images/mockupIcon.png"
          alt=""
          className="w-8 h-8"
        />
        <div className="flex items-center gap-2">
          <span className="bg-black text-white px-2 py-0.5 rounded text-xs font-bold">NEW!</span>
          <span className="text-sm font-bold text-gray-800">Smart Preview</span>
        </div>
      </div>
      {/* Desktop: at 40% boundary */}
      <div
        className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 items-center w-max px-3 rounded-full z-10"
        style={{
          backgroundColor: 'rgba(124, 51, 234, 0.2)',
          top: '40%',
        }}
      >
        <img
          src="/static/images/mockupIcon.png"
          alt=""
          className="w-8 h-8"
        />
        <div className="flex items-center gap-2">
          <span className="bg-black text-white px-2 py-0.5 rounded text-xs font-bold">NEW!</span>
          <span className="text-sm font-bold text-gray-800">Smart Preview</span>
        </div>
      </div>

      {/* Content Area - 40% of height below visualization */}
      <div
        className="w-full h-3/5 flex flex-col items-center justify-start px-2 py-2"
      >
        <div className="flex w-full items-center justify-center md:px-28 px-4">
          <div className="flex flex-col items-center justify-start">

            {/* Title */}
            <h2 className="m-0 p-0 text-xl md:text-2xl lg:text-3xl font-semibold text-gray-700 text-center mb-4 mt-8 md:mt-12">
              <FormattedMessage id="RoomMockupUpload.newTitle" />
            </h2>

            {/* Description */}
            <p className="m-0 p-0 text-xs md:text-sm lg:text-base text-gray-600 text-center mb-8 w-full break-words whitespace-pre-line">
              <FormattedMessage id="RoomMockupUpload.newDescription" />
            </p>
          </div>
        </div>

        {/* Upload Button Container with Mockup Icon */}
        <div className="relative flex items-center justify-center w-full">
          {/* Mockup Icon - Behind button */}
          <img
            src="/static/images/mockupIcon.png"
            alt=""
            className="absolute"
            style={{
              width: '150px',
              height: '150px',
              top: '-40px',
              opacity: 0.2,
              zIndex: 0,
            }}
          />

          {/* Upload Button */}
          <div className="relative bg-white rounded-full shadow-md md:shadow-[0px_-1px_14px_6px_rgba(0,0,0,0.25)] px-1 py-1 flex items-center md:gap-4 border border-2 border-black justify-between" style={{ zIndex: 1 }}>
            {/* CTA Text */}
            <div className="flex flex-col md:px-6 pl-3 pr-2 items-start">
              <span className="text-gray-700 italic font-light text-sm">
                <FormattedMessage id="RoomMockupUpload.ctaText" />
              </span>
              <span className="text-gray-700 text-sm md:text-sm m-0 font-semibold">Upload a picture of your room</span>
            </div>

            {/* Upload Button */}
            <label className="m-0 p-0 bg-black text-white px-4 py-2 rounded-full font-medium cursor-pointer transition-all duration-200 hover:bg-gray-800 active:scale-95 text-sm md:text-base">
              <FormattedMessage id="RoomMockupUpload.newUploadButton" />
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />
              <span className="ml-2 text-white">
                <IconCamera />
              </span>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {(localError || error) && (
          <div className="mt-3 px-3 py-2 md:px-4 md:py-3 bg-red-50 text-red-800 rounded-lg text-center text-xs md:text-sm max-w-md">
            {localError || error}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomMockupUpload;

